import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    Type,
    ViewChild
} from '@angular/core';
import {Subject, Subscription} from 'rxjs';
import {ICsPagingParams} from '@app/shared/cs-components/cs-components.interface';
import {CsAgGridJsScriptHandlerService} from '@app/shared/cs-ag-grid-js/cs-ag-grid-js-script-handler.service';
import {
    ICsAgGridJs,
    ICsDetailGridHeightObject,
    ICsGridAction,
    ICsGridApi
} from '@app/shared/cs-ag-grid-js/cs-ag-grid-js.interface';
import {
    CsAgGridJsAngularComponentResolverService
} from '@app/shared/cs-ag-grid-js/cs-ag-grid-js-angular-component-resolver.service';
import {environment} from '../../../environments/environment';
import cloneDeep from 'lodash.clonedeep';
import {GridApi} from "ag-grid-community";

declare var agGrid;

@Component({
    selector: 'app-cs-ag-grid-js',
    templateUrl: './cs-ag-grid-js.component.html',
    styleUrls: ['cs-ag-grid-js.component.scss']
})
export class CsAgGridJsComponent implements OnInit, OnDestroy {

    @ViewChild('grid') grid: ElementRef;

    @Input() config: ICsAgGridJs;
    @Input() params: any; // Should be passed only for child grid or detail grid

    @Output() onGridInit = new EventEmitter<ICsGridApi>();
    @Output() onGridDataLoad = new EventEmitter();
    @Output() onAllRowSelection = new EventEmitter();
    @Output() onRowSelection = new EventEmitter<{ data: object; isSelected: boolean }>();
    // @Output() sendParentNode = new EventEmitter()
    filterWidthChangeEvent = new Subject<any>();
    appliedFilter: any[] = [];
    searchText = '';
    totalRecords: number;
    paginationParams: ICsPagingParams = {};
    gridOptions: any;
    gridApi: any;
    gridLayout: 'normal' | 'autoHeight' = 'autoHeight';
    gridListAddOnData: any;
    gridList$: Subscription;
    isDataLoading = true;
    gridComputedHeight: number;
    private gridApiMap: Map<number | string, GridApi> = new Map();

    constructor(
        private _scriptHandler: CsAgGridJsScriptHandlerService,
        private _componentResolver: CsAgGridJsAngularComponentResolverService,
        private renderer: Renderer2
    ) {
    }

    async ngOnInit() {
        await this._scriptHandler.loadAgGridScript();
        this.cloneConfig();
        this.setConfig();
        this.setGridOptions();
        this.setGridLicense();
        this.createGrid();
        this.setCSRMData();
    }

    private cloneConfig() {
        this.config = cloneDeep(this.config);
    }

    private setConfig() {
        if (this.config.gridConfig.rowReOrder) {
            this.config.gridConfig.colDefs.unshift({
                field: 'row_drag',
                headerName: '',
                rowDrag: true,
                minWidth: 48,
                maxWidth: 48,
            });
        }

        const dataKeys = this.config.gridConfig.dataResKeys;
        this.config.gridConfig.dataResKeys = {
            countKey: dataKeys ? (dataKeys['countKey'] || 'count') : 'count',
            dataKey: dataKeys ? (dataKeys['dataKey'] || 'data') : 'data'
        };

        this.appliedFilter = this.config.hideFilterPanel ? [] : (this.config.filterPanelConfig['appliedFilter'] || []);

        this.filterWidthChangeEvent.next(this.appliedFilter);

        this.paginationParams['pageSize'] = this.isPagination ? (this.config.gridConfig.rowsPerPage || 5) : null;
    }

    private setGridOptions() {
        this.gridOptions = {
            components: {
                ...this._componentResolver.csAgCustomComponents(),
                ...this.resolvedAngularComponents()
            },

            // Note: It will be enabled (or) handled based on future spec, if required.
            // selectionColumnDef: {
            //     pinned: true
            // },

            defaultColDef: {
                flex: 1,
                suppressHeaderMenuButton: true,
                suppressHeaderContextMenu: true,
                sortable: false,
                unSortIcon: true,
                wrapText: this.config.gridConfig.wrapText !== false,
                autoHeight: this.config.gridConfig.wrapText !== false,
                wrapHeaderText: this.config.gridConfig.wrapHeaderText !== false,
                autoHeaderHeight: this.config.gridConfig.wrapHeaderText !== false,
                enableCellChangeFlash: true,
                resizable: this.config.gridConfig.isColumnResizable === true
            },

            columnDefs: this.config.gridConfig.colDefs,
            tooltipShowDelay: this.config.gridConfig.tooltipShowDelay,
            tooltipShowMode: this.config.gridConfig.tooltipShowMode,
            domLayout: 'autoHeight',
            suppressMovableColumns: true,
            rowClass: this.focusEnablingClass,
            suppressCellFocus: this.config.gridConfig.cellFocus !== true,
            suppressServerSideFullWidthLoadingRow: true,
            masterDetail: !!this.config.gridConfig.enableDetailGrid,
            suppressContextMenu: true,
            scrollbarWidth: 4,
            noRowsOverlayComponent: 'noRowsOverlay',
            suppressMoveWhenRowDragging: true,
            suppressPaginationPanel: true,
            suppressScrollOnNewData: true, // Prevents unnecessary multiple API calls
            blockLoadDebounceMillis: 500, // Ensures loading indicator stays visible for UX
            rowGroupPanelShow: this.config.gridConfig.enableRowGroupPanel ? 'always' : 'never',
            // keepDetailRows: true,
            // keepDetailRowsCount: 1,
            // Note: Below, will be enabled (or) handled based on the future spec, if required.
            // suppressCsvExport: true,
            // suppressExcelExport: true,
            // rowDragMultiRow: true,
            // rowDragEntireRow: true,
            // detailRowAutoHeight: true,

            onGridSizeChanged: (event: any) => this.updateDomLayout(),
            onCellClicked: (params: any) => this.onOpenChildGrid(this.config.gridConfig.isCollapseOtherParents, params),
            onModelUpdated: (event: any) => this.updateDomLayout(),
            onRowDragMove: (event: any) => this.swapRows(event),
            onDragStopped: (event: any) => this.onRowDropped(event),
            onRowSelected: (event: any) => this.rowSelection(event),
            onSelectionChanged: (event: any) => this.allRowSelection(event),
            onSortChanged: (event: any) => this.resetSelectedRows(),
            onCellFocused: this.config.gridConfig.onCellKeyDown
        };

        this.setServerSideProps();
        this.setPaginationProps();
        this.setRowSelectionConfig();
        this.setDetailGridConfig();
    }

    onOpenChildGrid(collapse: boolean, params: any) {
        // only act if expand/collapse icon clicked
        if (collapse && params.colDef.cellRenderer === 'detailCellRenderer' && params.node.master) {
            setTimeout(() => {
                if (params.node.expanded) {
                    this.gridApi.forEachNode(node => {
                        if (node !== params.node && node.expanded) {
                            node.setExpanded(false);   // collapse others
                            node['expandedColId'] = null;
                        }
                    });
                }
            });
        }
    }
    private setServerSideProps() {
        if (this.isCSRMEnabled) {
            return;
        }

        this.gridOptions['rowModelType'] = 'serverSide';
        this.gridOptions['serverSideStoreType'] = 'partial';
        this.gridOptions['serverSideInitialRowCount'] = this.config.gridConfig.loaderRowCount || 5;
        this.gridOptions['serverSideDatasource'] = this.dataSource;
        this.gridOptions['maxConcurrentDatasourceRequests'] = 1;
    }

    private setPaginationProps() {
        if (this.isPagination) {
            this.gridOptions['pagination'] = true;
            this.gridOptions['paginationPageSize'] = this.paginationParams['pageSize'];
            this.gridOptions['cacheBlockSize'] = this.paginationParams['pageSize'];
        }
    }

    get focusEnablingClass(): string {
        let enablingClass = '';
        const isSpace = () => enablingClass ? ' ' : '';

        enablingClass += this.config.gridConfig.rowFocus ? (isSpace() + 'cs-enable-row-focus') : '';
        enablingClass += this.config.gridConfig.cellFocus ? (isSpace() + 'cs-enable-cell-focus') : '';

        return enablingClass;
    }

    get isPagination() {
        return !this.isCSRMEnabled && !this.config.hidePagination;
    }

    private get isCSRMEnabled() {
        return this.config.gridConfig.enableClientSideRowModel;
    }

    private resolvedAngularComponents() {
        if (this.config['customComponents']) {
            const resolvedComponents = {};

            Object.entries(this.config.customComponents).forEach(([name, component]) => {
                resolvedComponents[name] = this._componentResolver.resolveAngularRendererComponent(component as Type<any>);
            });

            return resolvedComponents;
        }

        return null;
    }

    private setRowSelectionConfig() {
        if (this.config.gridConfig.rowSelection) {
            this.gridOptions['rowSelection'] = {
                mode: 'multiRow',
                // headerCheckbox: false
                isRowSelectable : this.config.gridConfig.hasOwnProperty("isRowSelectable") ? this.config.gridConfig.isRowSelectable:() => true,
            };
        }
        if(this.config.gridConfig.pinRowSelection) {
            this.gridOptions['selectionColumnDef'] = {
                pinned: this.config.gridConfig.pinRowSelection,
            };
        }
    }

    private setDetailGridConfig() {
        if (this.config.gridConfig.enableDetailGrid && this.config.detailGridConfig) {
            const detailConfig = this.config.detailGridConfig;

            const paramsCb = detailConfig.params;
            const rendererCb = detailConfig.renderer;
            // const heightCb = detailConfig.gridHeight;

            this.gridOptions['detailCellRenderer'] = (rendererParams: any): HTMLElement => {
                const detailMetaData = rendererParams.node.parent['detailMetaData'];
                // const detailHeight: ICsDetailGridHeightObject | number = heightCb ? heightCb(detailMetaData) : 310;
                const detailRenderer = rendererCb ? rendererCb(detailMetaData) : 'detailGridRenderer';
                const RendererClass = this.gridOptions.components[detailRenderer];
                const instance = new RendererClass();
                const rowId = (rendererParams.node.id || rendererParams.node.rowIndex).replace("detail_", "");
                this.gridApiMap.set(rowId, this.gridApi);
                rendererParams['currentParent'] = this.params && this.params['parentTree']
                    ? [...this.params['parentTree'], { api: this.gridApi, rowId: rowId, config: this.config}]
                    : [{ api: this.gridApi, rowId: rowId, config: this.config }];

                instance.init(rendererParams);
                // const rowNodes = this.getDisplayedRowNodes(rendererParams.api) || [];
                //
                // const computedHeight = typeof detailHeight == 'number' ? detailHeight : Math.min(
                //     (detailHeight.topBarHeight || 0) + detailHeight.headerHeight + (detailHeight.padding || 0) + rowNodes.length * detailHeight.rowHeight,
                //     detailHeight.maxHeight
                // );
                // setTimeout(() => {
                //     rendererParams.node.setRowHeight(computedHeight);
                //     rendererParams.api.onRowHeightChanged();
                // });

                return instance.getGui();
            };

            this.gridOptions['detailCellRendererParams'] = (params: any) => {
                return {config: paramsCb(params.node.parent['detailMetaData'])};
            };
        }
    }

    private setGridLicense() {
        (window as any).agGrid.LicenseManager.setLicenseKey(environment.agGridEnterpriseLicenseKey);
    }

    private createGrid() {
        // Step 1: Check if agGrid exists
        const agGrid = (window as any).agGrid;

        // Step 2: Log current status for debugging
        const status = this._scriptHandler.getScriptStatus();

        // Step 3: Verify agGrid and createGrid exist
        if (!agGrid) {
            this.reloadScriptAndCreateGrid();
            return;
        }

        if (!agGrid.createGrid) {
            this.reloadScriptAndCreateGrid();
            return;
        }

        // Step 4: Verify grid element exists
        if (!this.grid || !this.grid.nativeElement) {
            return;
        }

        // Step 5: Create grid
        try {
            this.gridApi = agGrid.createGrid(this.grid.nativeElement, this.gridOptions);

            if (!this.gridApi) {
                return;
            }
            this.onGridInit.emit(this.gridInstance);
            // this.updateDragListenersToHeaders();
            // this.observeGridContainerResize();
        } catch (error) {
            console.error('Error creating grid:', error);
            this.reloadScriptAndCreateGrid();
        }
    }

    private async reloadScriptAndCreateGrid() {
        try {

            // Reset the script handler state to allow reload
            this._scriptHandler.resetScriptState();

            await this._scriptHandler.loadAgGridScript();

            // Verify again after reload
            const status = this._scriptHandler.getScriptStatus();


            if (this._scriptHandler.isScriptLoaded()) {

                this.createGrid();
            } else {
                console.error('Script still not loaded after reload attempt');
                // Only reload page as last resort
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            }
        } catch (error) {
            console.error('Failed to reload script:', error);
            setTimeout(() => {
                window.location.reload();
            }, 2000);
        }
    }

    // Add this public method for debugging
    public verifyAgGridLoaded(): boolean {
        const agGrid = (window as any).agGrid;
        const status = {
            windowAgGrid: !!agGrid,
            createGrid: !!(agGrid && agGrid.createGrid),
            gridElement: !!(this.grid && this.grid.nativeElement),
            scriptStatus: this._scriptHandler.getScriptStatus()
        };


        return !!(agGrid && agGrid.createGrid);
    }

    private get dataSource() {
        return ({getRows: (params: any) => this.setGridData(params)});
    }

    private setCSRMData() {
        if (this.isCSRMEnabled) {
            this.setGridData();
        }
    }

    protected get gridInstance(): ICsGridApi {
        return {
            refreshGridData: this.refreshGridData,
            addOnData: this.getAddOnData,
            getSelectedRows: this.getSelectedRows,
            resetPagination: this.resetPagination,
            // refreshHeaders: this.updateDragListenersToHeaders.bind(this),
            gridApi: this.gridApi
        };
    }

    isDisable(action: ICsGridAction): boolean {
        return typeof action.disable === 'function' ? action.disable() : action.disable;
    }

    isHide(action: ICsGridAction): boolean {
        return typeof action.hide === 'function' ? action.hide() : action.hide;
    }

    swapRows = (event: any) => {
        if (!event.node
            || !event.overNode
            || !event.vDirection
            || (event.node.id === event.overNode.id)) {
            return;
        }

        requestAnimationFrame(() => {
            const draggedRowElement = this.grid.nativeElement.querySelector(`[row-id="${event.node.id}"]`);
            const overRowElement = this.grid.nativeElement.querySelector(`[row-id="${event.overNode.id}"]`);
            const draggingRowIndex = draggedRowElement.getAttribute('row-index');
            const overRowIndex = overRowElement.getAttribute('row-index');

            if ((event.vDirection === 'down' && (draggingRowIndex < overRowIndex))
                || (event.vDirection === 'up' && (draggingRowIndex > overRowIndex))) {

                const rowHeight = event.node.rowHeight;
                const draggingRowPos = +draggingRowIndex * rowHeight;
                const overRowPos = +overRowIndex * rowHeight;

                this.renderer.setAttribute(draggedRowElement, 'row-index', overRowIndex);
                this.renderer.setAttribute(overRowElement, 'row-index', draggingRowIndex);
                this.renderer.setStyle(draggedRowElement, 'transform', `translateY(${overRowPos}px)`);
                this.renderer.setStyle(overRowElement, 'transform', `translateY(${(draggingRowPos)}px)`);

                this.updateSwappingRowClasses(event, draggedRowElement, +overRowIndex);
                this.updateSwappingRowClasses(event, overRowElement, +draggingRowIndex);

                event.node.data['swappedOrder'] = +overRowIndex;
                event.overNode.data['swappedOrder'] = +draggingRowIndex;
            }
        });
    }

    updateSwappingRowClasses(event: any, rowElement: Element, rowIndex: number) {
        let totalRows = event.api.getDisplayedRowCount();
        totalRows = totalRows > this.paginationParams.pageSize ? this.paginationParams.pageSize : totalRows;

        this.renderer[rowIndex === 0 ? 'addClass' : 'removeClass'](rowElement, 'ag-row-first');
        this.renderer[rowIndex === (totalRows - 1) ? 'addClass' : 'removeClass'](rowElement, 'ag-row-last');

        this.renderer.addClass(rowElement, (rowIndex % 2) === 0 ? 'ag-row-even' : 'ag-row-odd');
        this.renderer.removeClass(rowElement, (rowIndex % 2) === 0 ? 'ag-row-odd' : 'ag-row-even');
    }

    onRowDropped(event: any) {
        if (this.config.gridConfig.rowReOrder) {
            const swappedRows = this.getDisplayedRowNodes(event.api)
                .filter((rowNode: any) => 'swappedOrder' in rowNode.data)
                .map((rowNode: any) => ({id: rowNode.data['id'], order: rowNode.data['swappedOrder']}))
                .sort((current, next) => current['order'] > next['order'] ? 1 : (current['order'] < next['order'] ? -1 : 0));

            if (swappedRows.length) {
                this.config.gridConfig
                    .updateRowOrder(swappedRows)
                    .subscribe(res => this.refreshGridData());
            }
        }
    }

    getDisplayedRowNodes = (api: any) => {
        return api.getRenderedNodes();
    }

    rowSelection(event: any) {
        if (event.source === 'uiSelectAll') {
            return;
        }

        const eventParam = {
            data: event.data,
            isSelected: event.node.isSelected()
        };

        const selectionCb = this.config.gridConfig.onRowSelection;

        selectionCb ? selectionCb(this.gridInstance, eventParam) : this.onRowSelection.emit(eventParam);
    }

    allRowSelection(event: any) {
        if (['apiSelectAll', 'checkboxSelected'].includes(event.source)) {
            return;
        }

        const allSelectionCb = this.config.gridConfig.onAllRowSelection;

        allSelectionCb ? allSelectionCb(this.gridInstance) : this.onAllRowSelection.emit();
    }

    getSelectedRows = (): object[] => {
        return this.getDisplayedRowNodes(this.gridApi)
            .filter((rowNode: any) => rowNode.isSelected())
            .map((rowNode: any) => rowNode.data);
    }

    get isHeaderActionsAvailable(): boolean {
        return !!(this.config.headerPanelConfig.actions.length
            && this.config.headerPanelConfig.actions.find(action => !this.isHide(action)));
    }

    getAddOnData = () => {
        return this.gridListAddOnData || null;
    }

    onActionClick(action: ICsGridAction) {
        if (this.isDisable(action)) {
            return;
        }

        action.onClick({action: action.id});
    }

    onSearch(val: string) {
        this.searchText = val;
        this.resetPagination();
        this.refreshGridData();
    }

    onFilter(param: object | number, action: 'apply' | 'remove' | 'update') {
        if (action === 'apply') {
            this.appliedFilter.push(param);
        } else if (action === 'remove') {
            this.appliedFilter.splice(param as number, 1);
        } else if(action === 'update') {
            const index = param['index'];
            delete param['index'];
            this.appliedFilter[index] = param;
        }

        this.resetPagination();
        this.refreshGridData();
    }

    updatePagingParams(params: ICsPagingParams) {
        this.paginationParams = params;

        if (params.currentEvent && params.currentEvent !== 'none') {
            this.gridApi.setGridOption('paginationPageSize', this.paginationParams.pageSize);
            this.gridApi.setGridOption('cacheBlockSize', this.paginationParams.pageSize);
            this.goToPage();
            this.refreshGridData();
        }
    }

    goToPage(page?: number) {
        if (this.isPagination) {
            if (page) {
                this.paginationParams.currentPage = page;
            }
            this.gridApi.paginationGoToPage(this.paginationParams.currentPage);
        }
    }

    updateDomLayout() {
        const gridEl = this.grid.nativeElement as HTMLElement;
        const gridViewportEl = gridEl.querySelector('.ag-body-viewport .ag-viewport.ag-center-cols-viewport') as HTMLElement;

        if (!gridViewportEl) {
            return;
        }

        const containerHeight = gridEl.clientHeight;
        const headerHeight = this.gridApi.getSizesForCurrentTheme().headerHeight;
        const headerBorderWidth = 1;
        const gridHeight = headerHeight + headerBorderWidth + gridViewportEl.offsetHeight;
        const domLayout = gridHeight > containerHeight ? 'normal' : 'autoHeight';

        if (domLayout !== this.gridLayout) {
            this.gridApi.setGridOption('domLayout', domLayout);
            this.gridLayout = domLayout;
        }
        if(this.params) {
            const defaultRowHeight = 310;
            const rowNodes = this.getDisplayedRowNodes(this.gridApi);
            const detailHeight: ICsDetailGridHeightObject | number = this.params.config.gridHeight && rowNodes.length ? this.params.config.gridHeight : defaultRowHeight;
            const computedHeight = typeof detailHeight == 'number' ? detailHeight : Math.min(
                (detailHeight.topBarHeight || 0) + detailHeight.headerHeight + (detailHeight.padding || 0) + rowNodes.length * detailHeight.rowHeight,
                detailHeight.maxHeight
            );
            // const detailGridFound = rowNodes.find((node) => node.detail === true);
            // this.sendParentNode.emit(detailGridFound);

            if(this.params.parentTree.length == 2 && this.gridComputedHeight !== computedHeight-42) {
                const parentBeforeRowNode = this.params.parentTree[1];
                const parentBeforeRowNodes = this.getDisplayedRowNodes(parentBeforeRowNode.api);
                const count = parentBeforeRowNodes.filter(node => {
                    const id = node.id;
                    return typeof id === 'string' && !id.includes('detail_');
                }).length;
                const parentDetailHeight: ICsDetailGridHeightObject | number = parentBeforeRowNode.config.gridHeight && rowNodes.length ? parentBeforeRowNode.config.gridHeight : defaultRowHeight;
                const parentComputedHeight = typeof parentDetailHeight == 'number' ? parentDetailHeight : (parentDetailHeight.topBarHeight || 0) + parentDetailHeight.headerHeight + (parentDetailHeight.padding || 0) + count * parentDetailHeight.rowHeight
                const parentHeight = typeof parentDetailHeight == 'number' ? parentDetailHeight : Math.min(((computedHeight*(parentBeforeRowNodes.length-count))+parentComputedHeight), parentDetailHeight.maxHeight);
                const grandParentRowNode = this.params.parentTree[0];
                grandParentRowNode.api.getRowNode('detail_'+grandParentRowNode.rowId).setRowHeight(parentHeight);
                grandParentRowNode.api.onRowHeightChanged();
                this.params.node.setRowHeight(computedHeight);
                this.params.api.onRowHeightChanged();
            }
            else if(this.params.parentTree.length == 1 && this.gridComputedHeight !== computedHeight) {
                this.params.node.setRowHeight(computedHeight);
                this.params.api.onRowHeightChanged();
            }
            this.gridComputedHeight = computedHeight;
        }
    }

    showOrHideNoRowsOverlay = (show: boolean) => this.gridApi[show ? 'showNoRowsOverlay' : 'hideOverlay']();

    refreshGridData = () => {
        this.showOrHideNoRowsOverlay(false);
        if (this.isCSRMEnabled) {
            this.setCSRMData();
        } else {
            this.gridApi.refreshServerSide({route: [], purge: true});
            this.gridApi.setGridOption('serverSideDatasource', this.dataSource);
        }
        this.resetSelectedRows();
    }

    private setGridData(params?: any) {
        const requestParams = {
            searchText: this.searchText,
            filters: this.appliedFilter ? JSON.stringify(this.appliedFilter) : null
        };

        if (!this.isCSRMEnabled && params) {
            requestParams['page'] = this.isPagination ? (this.paginationParams.currentPage || 1) : null;
            requestParams['limit'] = this.paginationParams.pageSize;
            requestParams['sort'] = params.request.sortModel[0] ? JSON.stringify(params.request.sortModel[0]) : null;
            requestParams['groupKeys'] = params.request.groupKeys ? JSON.stringify(params.request.groupKeys) : null;
            requestParams['rowGroupCols'] = params.request.rowGroupCols
                ? JSON.stringify(params.request.rowGroupCols.map((item: object) => item['field']))
                : null;
        }

        this.gridList$ = this.config.gridConfig.gridList({reqParams: requestParams})
            .subscribe(res => {
                this.totalRecords = +res[this.config.gridConfig.dataResKeys.countKey];
                const data = res[this.config.gridConfig.dataResKeys.dataKey];
                this.gridListAddOnData = {...res['addOnData'], displayRecordCount: data.length};

                if (this.isCSRMEnabled) {
                    this.showOrHideNoRowsOverlay(!this.totalRecords);
                    this.gridApi.setGridOption('rowData', data);
                } else {
                    const isPartialRecordsInPage = data.length < requestParams['limit'];
                    const recordCount = isPartialRecordsInPage ? data.length : this.totalRecords;

                    this.showOrHideNoRowsOverlay(!recordCount);
                    params.success({rowData: data, rowCount: recordCount});
                }

                this.isDataLoading = false;

                this.config.gridConfig.onGridDataLoad ?
                    this.config.gridConfig.onGridDataLoad(this.gridInstance)
                    : this.onGridDataLoad.emit(this.gridInstance);
            });
    }

    resetPagination = () => {
        if (this.isPagination) {
            this.goToPage(1);
            this.totalRecords = null;
            this.isDataLoading = true;
        }
    }

    resetSelectedRows() {
        if (this.config.gridConfig.rowSelection) {
            this.gridApi.deselectAll();
        }
    }

    ngOnDestroy() {
        if (this.gridList$) {
            this.gridList$.unsubscribe();
        }
        this._scriptHandler.removeAgGridScript();
    }
}
